/**
 * DenyNotesSheet — FE-227 v2 / Decline-with-notes.
 *
 * Bottom-sheet modal mounted from every surface that surfaces a
 * "Decline borrow request" action:
 *
 *   1. PendingRequestCard (inline strip on GroupDetail + ItemDetail)
 *   2. NotificationCard via useNotificationActions.declineBorrow
 *   3. BorrowRequestsManager (replaces its bespoke inline modal)
 *
 * Why a shared component instead of inlining: the message-input UX
 * needs to stay identical across all three surfaces so the user
 * builds one mental model. Inlining would risk three subtly
 * different implementations drifting apart (char cap, placeholder,
 * empty-submit behavior, etc.).
 *
 * Backend contract (BE-214, shipped months ago):
 *   PATCH /users/borrow-requests/{id}
 *   Body: { action: 'DENY', notes: '...' }      ← BE-214 alias
 *      OR { action: 'DENY', response_message: '...' }  ← legacy alias
 *   Both work. Our `denyBorrowRequest` thunk uses `response_message`.
 *
 * Persistence (per backend invariant I-8):
 *   - The message lives in TWO server-side stores:
 *     1. `borrow_request.response_message` (DB column, kept forever)
 *     2. `notification.payload.response_message` (denormalized
 *        snapshot in the BORROW_DECLINED row, also kept forever)
 *   - No FE caching needed — the requester re-fetches both surfaces
 *     across devices / logouts / sessions.
 *
 * Empty submit is allowed — the textarea is optional. Submitting
 * with no text sends `response_message: undefined`, which the
 * backend treats as "no reason given" (renders the notification
 * without a quoted block, requester sees just "Marcus declined
 * your request").
 */

import React, { useEffect, useState } from 'react';
import {
  ActivityIndicator,
  Modal,
  Platform,
  Pressable,
  StyleSheet,
  Text,
  TextInput,
  View,
} from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';
import Toast from 'react-native-toast-message';

import { useAppDispatch } from '../../../store/store';
import { fpColors } from '../../../styles/theme';
import { denyBorrowRequest, refreshAllRequests } from '../borrowRequestsSlice';
import type { BorrowRequestDetails } from '../../../api/borrowTypes';

/** Maximum length for the optional decline reason. Mirrors the
 *  500-char cap on `borrow_message` (the requester's side of the
 *  conversation) for symmetry — neither party can write War & Peace
 *  on a notification card. */
const MAX_NOTE_LENGTH = 500;

export interface DenyNotesSheetProps {
  visible: boolean;
  /** The request we're declining. The card header reads requester
   *  name, item name, and the requester's original message off this
   *  shape — minimal context so the owner remembers what they're
   *  saying no to. */
  request: BorrowRequestDetails | null;
  onClose: () => void;
  /** Fires after a successful decline. Caller is responsible for
   *  any list refresh / parent-state cleanup. */
  onSubmitted?: () => void;
}

export const DenyNotesSheet: React.FC<DenyNotesSheetProps> = ({
  visible,
  request,
  onClose,
  onSubmitted,
}) => {
  const dispatch = useAppDispatch();
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Reset internal state on every fresh open so a previously-typed
  // note from another request doesn't bleed in.
  useEffect(() => {
    if (visible) {
      setNote('');
      setSubmitting(false);
    }
  }, [visible]);

  const handleSubmit = async () => {
    if (submitting || !request) return;
    setSubmitting(true);
    const trimmed = note.trim();
    try {
      await dispatch(
        denyBorrowRequest({
          requestId: request.id,
          responseMessage: trimmed || undefined,
        }) as any,
      ).unwrap();
      Toast.show({
        type: 'success',
        text1: 'Declined',
        text2: `${formatShortName(request.requester_name)} has been notified.`,
      });
      onSubmitted?.();
      onClose();
    } catch (err: any) {
      // The slice's rejectWithValue surfaces the BE `detail` field
      // ("Borrow request not found or cannot be modified" on stale-
      // state 404s, etc.) as the thunk error. Show the user the real
      // reason rather than the generic "Please try again."
      const message =
        typeof err === 'string'
          ? err
          : err?.message ?? 'Please try again.';
      const isStale =
        typeof message === 'string' &&
        /not found|cannot be modified|already|expired/i.test(message);
      Toast.show({
        type: 'error',
        text1: isStale ? 'Already actioned' : "Couldn't complete",
        text2: isStale
          ? "This request was already approved, declined, or expired. Refreshing the list."
          : message,
      });
      // Stale rows still showing the Deny CTA are a UX bug we can't
      // fix without a fresh fetch — pull the latest list so the
      // already-actioned row drops out of the inbox.
      if (isStale) {
        dispatch(refreshAllRequests() as any);
        onClose();
      }
      setSubmitting(false);
    }
  };

  if (!visible || !request) return null;

  const requesterName = formatShortName(request.requester_name);
  const itemName = request.item_name ?? 'this item';
  const requesterMessage = (request.message ?? '').trim();
  const charCount = note.length;
  const overLimit = charCount > MAX_NOTE_LENGTH;
  const timeAgo = formatTimeAgo(request.created_at);

  return (
    <Modal
      visible={visible}
      transparent
      // FE-206 — Was animationType="fade", which cross-fades the entire
      // modal (backdrop + content) from opacity 0 → 1. During the
      // first ~200ms the underlying view bled through both layers
      // and pending-request strip text from GroupDetail showed up
      // behind / on top of the sheet content. "slide" enters the
      // sheet from off-screen with the backdrop animating
      // independently — no transparent intermediate state. Same
      // pattern used by HandOffSheet, RecallSheet, and the rest of
      // the bottom-sheet family.
      animationType="slide"
      onRequestClose={onClose}
    >
      <View style={styles.backdrop}>
        <Pressable style={styles.backdropPress} onPress={onClose} />
        <View style={styles.sheet}>
          {/* Header */}
          <View style={styles.header}>
            <Text style={styles.headerTitle}>Decline request</Text>
            <Pressable
              onPress={onClose}
              accessibilityRole="button"
              accessibilityLabel="Close"
              hitSlop={8}
            >
              <MaterialIcons name="close" size={20} color={fpColors.onSurface} />
            </Pressable>
          </View>

          {/* Requester context */}
          <View style={styles.contextBlock}>
            <View style={styles.avatar}>
              <Text style={styles.avatarText}>
                {initialsFor(request.requester_name)}
              </Text>
            </View>
            <View style={styles.contextText}>
              <Text style={styles.contextName} numberOfLines={1}>
                {requesterName} wants to borrow
              </Text>
              <Text style={styles.contextItem} numberOfLines={1}>
                {itemName}
              </Text>
              {requesterMessage ? (
                <Text style={styles.contextMessage} numberOfLines={2}>
                  "{requesterMessage}"
                </Text>
              ) : null}
              {timeAgo ? (
                <Text style={styles.contextMeta}>{timeAgo}</Text>
              ) : null}
            </View>
          </View>

          {/* Notes input */}
          <View style={styles.field}>
            <Text style={styles.fieldLabel}>Add a note (optional)</Text>
            <TextInput
              value={note}
              onChangeText={setNote}
              placeholder='e.g. "I need it this weekend — try Monday?"'
              placeholderTextColor={fpColors.onSurfaceVariant}
              multiline
              maxLength={MAX_NOTE_LENGTH + 50 /* slight headroom for UX */}
              style={styles.textArea}
              accessibilityLabel="Decline note"
              testID="deny-notes-input"
              editable={!submitting}
            />
            <View style={styles.charRow}>
              <Text
                style={[
                  styles.charText,
                  overLimit && styles.charTextOver,
                ]}
              >
                {charCount} / {MAX_NOTE_LENGTH}
              </Text>
            </View>
          </View>

          <View style={styles.helperRow}>
            <MaterialIcons
              name="info-outline"
              size={14}
              color={fpColors.onSurfaceVariant}
            />
            <Text style={styles.helperText} numberOfLines={2}>
              {requesterName} will see your note in their inbox. Leave it
              blank to decline without a reason.
            </Text>
          </View>

          {/* Footer actions */}
          <View style={styles.footer}>
            <Pressable
              onPress={onClose}
              disabled={submitting}
              style={({ pressed }) => [
                styles.btn,
                styles.btnSecondary,
                (pressed || submitting) && { opacity: 0.85 },
              ]}
              accessibilityRole="button"
              accessibilityLabel="Cancel"
              testID="deny-notes-cancel"
            >
              <Text style={styles.btnSecondaryText}>Cancel</Text>
            </Pressable>
            <Pressable
              onPress={handleSubmit}
              disabled={submitting || overLimit}
              style={({ pressed }) => [
                styles.btn,
                styles.btnPrimary,
                pressed && { opacity: 0.85 },
                (submitting || overLimit) && { opacity: 0.55 },
              ]}
              accessibilityRole="button"
              accessibilityLabel="Decline request"
              testID="deny-notes-submit"
            >
              {submitting ? (
                <ActivityIndicator size="small" color={fpColors.onPrimary} />
              ) : (
                <Text style={styles.btnPrimaryText}>Decline</Text>
              )}
            </Pressable>
          </View>
        </View>
      </View>
    </Modal>
  );
};

// ── helpers ──────────────────────────────────────────────────────────

function formatShortName(full: string | null | undefined): string {
  const trimmed = (full ?? '').trim();
  if (!trimmed) return 'Someone';
  const parts = trimmed.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0]} ${parts[1][0].toUpperCase()}.`;
  }
  return parts[0];
}

function initialsFor(full: string | null | undefined): string {
  const trimmed = (full ?? '').trim();
  if (!trimmed) return '??';
  const parts = trimmed.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }
  return trimmed.slice(0, 2).toUpperCase();
}

function formatTimeAgo(iso: string | null | undefined): string {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const diffMs = Date.now() - then;
  if (diffMs < 0) return 'just now';
  const mins = Math.floor(diffMs / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

// ── styles ───────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  backdrop: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.45)',
    justifyContent: 'flex-end',
  },
  backdropPress: {
    ...StyleSheet.absoluteFillObject,
  },
  sheet: {
    backgroundColor: fpColors.surface,
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    paddingHorizontal: 20,
    paddingTop: 16,
    paddingBottom: Platform.OS === 'web' ? 20 : 36,
    maxWidth: Platform.OS === 'web' ? 440 : undefined,
    alignSelf: Platform.OS === 'web' ? 'center' : 'auto',
    width: Platform.OS === 'web' ? '100%' : undefined,
    marginHorizontal: Platform.OS === 'web' ? 16 : 0,
    marginBottom: Platform.OS === 'web' ? 24 : 0,
    borderBottomLeftRadius: Platform.OS === 'web' ? 20 : 0,
    borderBottomRightRadius: Platform.OS === 'web' ? 20 : 0,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 14,
  },
  headerTitle: {
    fontFamily: 'Inter_700Bold',
    fontSize: 17,
    color: fpColors.onSurface,
  },
  contextBlock: {
    flexDirection: 'row',
    gap: 12,
    paddingVertical: 12,
    paddingHorizontal: 12,
    borderRadius: 12,
    backgroundColor:
      (fpColors as any).surfaceContainerLow ??
      fpColors.surfaceVariant ??
      '#F5F0F7',
    marginBottom: 16,
  },
  avatar: {
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor:
      (fpColors as any).primaryContainer ?? '#EADDFF',
    alignItems: 'center',
    justifyContent: 'center',
  },
  avatarText: {
    fontFamily: 'Inter_700Bold',
    fontSize: 12,
    color: (fpColors as any).onPrimaryContainer ?? fpColors.primary,
  },
  contextText: {
    flex: 1,
    gap: 2,
  },
  contextName: {
    fontFamily: 'Inter_400Regular',
    fontSize: 13,
    color: fpColors.onSurface,
  },
  contextItem: {
    fontFamily: 'Inter_700Bold',
    fontSize: 14,
    color: fpColors.primary,
  },
  contextMessage: {
    fontFamily: 'Inter_400Regular',
    fontSize: 12,
    color: fpColors.onSurfaceVariant,
    fontStyle: 'italic',
    marginTop: 3,
  },
  contextMeta: {
    fontFamily: 'Inter_400Regular',
    fontSize: 11,
    color: fpColors.onSurfaceVariant,
    marginTop: 3,
  },
  field: {
    marginBottom: 12,
  },
  fieldLabel: {
    fontFamily: 'Inter_600SemiBold',
    fontSize: 13,
    color: fpColors.onSurface,
    marginBottom: 8,
  },
  textArea: {
    minHeight: 88,
    maxHeight: 160,
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: fpColors.outlineVariant ?? '#CAC4D0',
    backgroundColor: fpColors.surface,
    fontFamily: 'Inter_400Regular',
    fontSize: 14,
    color: fpColors.onSurface,
    textAlignVertical: 'top',
  },
  charRow: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    marginTop: 4,
  },
  charText: {
    fontFamily: 'Inter_400Regular',
    fontSize: 11,
    color: fpColors.onSurfaceVariant,
  },
  charTextOver: {
    color: fpColors.error,
  },
  helperRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 6,
    marginBottom: 16,
  },
  helperText: {
    flex: 1,
    fontFamily: 'Inter_400Regular',
    fontSize: 12,
    color: fpColors.onSurfaceVariant,
    lineHeight: 16,
  },
  footer: {
    flexDirection: 'row',
    gap: 12,
  },
  btn: {
    flex: 1,
    paddingVertical: 12,
    borderRadius: 999,
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 44,
  },
  btnPrimary: {
    backgroundColor: fpColors.primary,
  },
  btnPrimaryText: {
    fontFamily: 'Inter_700Bold',
    fontSize: 14,
    color: fpColors.onPrimary,
    letterSpacing: 0.2,
  },
  btnSecondary: {
    backgroundColor: fpColors.surface,
    borderWidth: 1,
    borderColor: fpColors.outline,
  },
  btnSecondaryText: {
    fontFamily: 'Inter_700Bold',
    fontSize: 14,
    color: fpColors.primary,
    letterSpacing: 0.2,
  },
});
